import React, { useState, useEffect, useRef } from "react";
import { AdjustmentsVerticalIcon } from "@heroicons/react/24/outline";
import AllMatchesTable from "./tables/AllMatchesTable";
import {
  useDeleteMatchLeadMutation,
  useGetAllFilteredMatchesQuery,
  useGetAllMatchesInfoQuery,
  useLazyGetAllFilteredMatchesQuery,
  useLazyGetAllMatchesInfoQuery,
} from "../../../../store/admin/matchesInfo";
import Loader from "../../../commons/Loader";
import { calendarData, comparedYearData, leadsDataCard } from "../adminData";
import DashboardAdminCalendar from "../../../calendar/DashboardAdminCalendar";
import moment from "moment";
import { formatDate } from "../../../../utilities/utils";
import {
  useGetAdminMetricsQuery,
  useLazyGetAdminMetricsQuery,
} from "../../../../store/admin/adminMetrics";
import GreenChartSvg from "../svgIcons/GreenChartSvg";
import RedChartSvg from "../svgIcons/RedChartSvg";
import DeleteSmsModal from "../../../modal/DeleteSmsModal";
import { toast } from "react-toastify";
import NumberSkeleton from "../../../commons/Skeleton/NumberSkeleton";

import CreditSlideOver from "../../../modal/CreditSlideOver";
const AllMatchesPage = () => {
  const calendarRef = useRef(null)
  const [metricsData, setMetricsData] = useState({});
  const [allMatchesData, setAllMatchesData] = useState();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLead, setDeleteLead] = useState(null);
  const currentDate = moment();
  const startOfMonth = currentDate.startOf("month");
  const [selectRange, setSelectRange] = useState({
    startDate:  moment().format("MM/DD/YYYY"),
    endDate: moment().format("MM/DD/YYYY"),
  });

  const [selectedCalendarFilter, setSelectedCalendarFilter] =
    useState("Today");
  const [selectedCompareFilter, setSelectedCompareFilter] = useState([
    "Previous period",
  ]);

  const [
    deleteMatchedLead,
    {
      data: deleteMatchedLeadData,
      isSuccess: deleteMatchedLeadSuccess,
      isLoading: deleteMatchedLeadLoader,
      isError: deleteMatchedLeadIsError,
      error: deleteMatchedLeadError,
    },
  ] = useDeleteMatchLeadMutation();

  const {
    data: allAdminMetricsData,
    isSuccess: allAdminMetricsDataSuccess,
    isLoading: allAdminMetricsDataLoader,
  } = useGetAdminMetricsQuery({
    startDate: startOfMonth.format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });

  const [
    getAllAdminMetricsData,
    {
      data: adminMetricsData,
      isLoading: adminMetricsDataLoader,
      isSuccess: adminMetricsDataSuccess,
      isFetching: adminMetricsDataFetching,
    },
  ] = useLazyGetAdminMetricsQuery();

  const {
    data: allFilteredMatchesInfo,
    isLoading: allFilteredMatchesInfoLoader,
    isSuccess: allFilteredMatchesInfoSuccess,
  } = useGetAllFilteredMatchesQuery({
    page: 1,
    startDate: startOfMonth.format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
  });

  const [
    getAllFilteredMatches,
    {
      data: filteredMatches,
      isSuccess: filteredMatchesSuccess,
      isLoading: filteredMatchesLoader,
      isFetching: isFetchingFilteredMatches,
    },
  ] = useLazyGetAllFilteredMatchesQuery();

  const [refetch, { data, isFetching }] = useLazyGetAllMatchesInfoQuery();

  const handleOutsideClick = (event) => {
    if (calendarRef.current && !calendarRef.current.contains(event.target)) {
      setCalendarOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (!deleteMatchedLeadLoader) {
      if (deleteMatchedLeadSuccess) {
        toast.success(deleteMatchedLeadData.message);
        refetch();
      } else if (deleteMatchedLeadIsError && deleteMatchedLeadError) {
        toast.error("Something went wrong, please try again");
      }
      setShowDeleteModal(false);
      setDeleteLead(null);
    }
  }, [deleteMatchedLeadSuccess, deleteMatchedLeadIsError]);

  useEffect(() => {
    if (adminMetricsDataSuccess && !adminMetricsDataLoader) {
      setMetricsData(adminMetricsData?.data);
      setCalendarOpen(false);
    }
  }, [adminMetricsDataSuccess, adminMetricsDataFetching]);

  useEffect(() => {
    if (allAdminMetricsDataSuccess && !allAdminMetricsDataLoader) {
      setMetricsData(allAdminMetricsData?.data);
    }
  }, [allAdminMetricsDataSuccess]);

  useEffect(() => {
    if (filteredMatchesSuccess && !filteredMatchesLoader) {
      setCalendarOpen(false);
    }
  }, [filteredMatchesSuccess, isFetchingFilteredMatches]);

  useEffect(() => {
    if (allFilteredMatchesInfo) {
      setAllMatchesData(allFilteredMatchesInfo);
    }
  }, [allFilteredMatchesInfo]);

  useEffect(() => {
    if (filteredMatches) {
      setAllMatchesData(filteredMatches);
    }
  }, [filteredMatches]);

  useEffect(() => {
    if (data) {
      setAllMatchesData(data);
    }
  }, [data]);

  useEffect(() => {
    if (isFetching) {
      setAllMatchesData({});
    }
  }, [isFetching]);

  const calendarFilterChange = (value) => {
    setSelectedCalendarFilter(value);

    const selectedPeriod =
      calendarData && calendarData?.find((item) => item.text === value)?.period;

    if (selectedPeriod) {
      setSelectRange({
        startDate: selectedPeriod.start,
        endDate: selectedPeriod.end,
      });
    } else {
      setSelectRange({});
    }
  };

  const compareFilterChange = (value) => {
    setSelectedCompareFilter(value);
  };

  const handleCalendarChange = (value, event) => {
    const startDate = formatDate(value[0]);
    const endDate = formatDate(value[1]);
    setSelectRange({ startDate: startDate, endDate: endDate });
    setSelectedCalendarFilter("Custom date");
  };

  const handleDateFilter = () => {
    const formattedStartDate = moment(
      selectRange.startDate,
      "MM/DD/YYYY"
    ).format("YYYY-MM-DD");
    const formattedEndDate = moment(selectRange.endDate, "MM/DD/YYYY").format(
      "YYYY-MM-DD"
    );
    if (selectedCalendarFilter === "All") {
      getAllAdminMetricsData({});
    } else {
      getAllAdminMetricsData({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    }

    if (selectedCalendarFilter === "All") {
      getAllFilteredMatches({});
    } else {
      getAllFilteredMatches({
        page: 1,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    }
  };

  const handleDropDownMenu = (eventType, data) => {
    switch (eventType) {
      case "delete":
        setShowDeleteModal(true);
        setDeleteLead(data);
        break;
    }
  };

  const handleOpen = () => {
    setCalendarOpen((prevState) => !prevState);
  };

  const handleClose = () => {
    setCalendarOpen(false);
  };

  // if (filteredMatchesLoader) return <Loader isLoading={true} />;

  return (
    <div className="col-span-12">
      <div className="flex justify-between items-start md:items-center flex-col md:flex-row gap-5 mb-5">
        <h3 className="text-xl leading-[160%] font-bold text-[#04091E]">
          Successful Matches
        </h3>
        <div className="flex items-start md:items-center gap-3.5 md:gap-[30px] flex-col md:flex-row md:max-w-[710px] justify-between w-full">
          <div className="w-full" ref={calendarRef}>
            <DashboardAdminCalendar
              calendarFilter={calendarData}
              compareFilter={comparedYearData}
              selectedCalendarFilter={selectedCalendarFilter}
              calendarFilterChange={calendarFilterChange}
              selectedCompareFilter={selectedCompareFilter}
              compareFilterChange={compareFilterChange}
              handleCalendarChange={handleCalendarChange}
              setSelectRange={setSelectRange}
              selectRange={selectRange}
              handleDateFilter={handleDateFilter}
              calendarOpen={calendarOpen}
              setCalendarOpen={setCalendarOpen}
              handleOpen={handleOpen}
              handleClose={handleClose}
            />
          </div>
          <button className="w-full md:w-12 h-12 flex items-center justify-center gap-2.5 md:gap-0 rounded-[10px] bg-[#EBEFF3]">
            <AdjustmentsVerticalIcon className="w-6 h-6 text-[#485868]" />
            <span className="text-sm font-bold leading-[160%] text-[#485868] block md:hidden">
              Filter
            </span>
          </button>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-5 xxl:gap-6 mb-[30px]">
        <div className="bg-white border border-[#EBEFF3] p-6 col-span-12 sm:col-span-6 xxl:col-span-4 rounded-md">
          <h6 className="text-base font-bold leading-[160%] text-customGray mb-6 capitalize">
            Successful Matches
          </h6>
          <div className="flex justify-between items-end">
            <div className="flex items-center gap-4">
              <span className="text-[32px] leading-[125%] font-black text-customGray">
                {metricsData?.successful_matches??<NumberSkeleton/>}
              </span>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5">
                  {metricsData?.successful_matches_change?.startsWith("-") ? (
                    <RedChartSvg />
                  ) : (
                    <GreenChartSvg />
                  )}
                  <span
                    className="text-sm font-bold leading-[180%]"
                    style={{
                      color: metricsData?.successful_matches_change?.startsWith(
                        "-"
                      )
                        ? "#EF2121"
                        : "#16CA7C",
                    }}
                  >
                    {metricsData?.successful_matches_change ?? "0%"}
                  </span>
                </div>

                <p className="text-xs font-medium leading-[160%] text-[#485868]">
                  from previous period
                </p>
              </div>
            </div>
            <div className="w-[57px] grid grid-cols-12 gap-1 items-end">
              <div className="col-span-6 h-6 bg-[#BEC2C5] rounded-sm"></div>
              <div className="col-span-6 h-16 bg-primary rounded-sm"></div>
            </div>
          </div>
        </div>
        <div className="bg-white border border-[#EBEFF3] p-6 col-span-12 sm:col-span-6 xxl:col-span-4 rounded-md">
          <h6 className="text-base font-bold leading-[160%] text-customGray mb-6 capitalize">
            Pending Matches
          </h6>
          <div className="flex justify-between items-end">
            <div className="flex items-center gap-4">
              <span className="text-[32px] leading-[125%] font-black text-customGray">
                {metricsData?.pending_matches??<NumberSkeleton/>}
              </span>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5">
                  {metricsData?.pending_matches_change?.startsWith("-") ? (
                    <RedChartSvg />
                  ) : (
                    <GreenChartSvg />
                  )}
                  <span
                    className="text-sm font-bold leading-[180%]"
                    style={{
                      color: metricsData?.pending_matches_change?.startsWith(
                        "-"
                      )
                        ? "#EF2121"
                        : "#16CA7C",
                    }}
                  >
                    {metricsData?.pending_matches_change ?? "1%"}
                  </span>
                </div>

                <p className="text-xs font-medium leading-[160%] text-[#485868]">
                  from previous period
                </p>
              </div>
            </div>
            <div className="w-[57px] grid grid-cols-12 gap-1 items-end">
              <div className="col-span-6 h-6 bg-[#BEC2C5] rounded-sm"></div>
              <div className="col-span-6 h-16 bg-primary rounded-sm"></div>
            </div>
          </div>
        </div>
        <div className="bg-white border border-[#EBEFF3] p-6 col-span-12 sm:col-span-6 xxl:col-span-4 rounded-md">
          <h6 className="text-base font-bold leading-[160%] text-customGray mb-6 capitalize">
            Failed Matches
          </h6>
          <div className="flex justify-between items-end">
            <div className="flex items-center gap-4">
              <span className="text-[32px] leading-[125%] font-black text-customGray">
                {metricsData?.failed_matches??<NumberSkeleton/>}
              </span>
              <div className="text-center">
                <div className="flex items-center justify-center gap-1.5">
                  {metricsData?.failed_matches_change?.startsWith("-") ? (
                    <RedChartSvg />
                  ) : (
                    <GreenChartSvg />
                  )}
                  <span
                    className="text-sm font-bold leading-[180%]"
                    style={{
                      color: metricsData?.failed_matches_change?.startsWith("-")
                        ? "#EF2121"
                        : "#16CA7C",
                    }}
                  >
                    {metricsData?.failed_matches_change ?? "1%"}
                  </span>
                </div>

                <p className="text-xs font-medium leading-[160%] text-[#485868]">
                  from previous period
                </p>
              </div>
            </div>
            <div className="w-[57px] grid grid-cols-12 gap-1 items-end">
              <div className="col-span-6 h-6 bg-[#BEC2C5] rounded-sm"></div>
              <div className="col-span-6 h-16 bg-primary rounded-sm"></div>
            </div>
          </div>
        </div>
      </div>
      <AllMatchesTable
        matchesData={allMatchesData}
        getUpdateData={(page, perPage) => {
          getAllFilteredMatches({
            page,
            perPage,
            startDate: moment(selectRange.startDate, "MM/DD/YYYY").format(
              "YYYY-MM-DD"
            ),
            endDate: moment(selectRange.endDate, "MM/DD/YYYY").format(
              "YYYY-MM-DD"
            ),
          });
        }}
        handleDropDownMenu={handleDropDownMenu}
      />
      <DeleteSmsModal
        isOpen={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleDelete={() => deleteMatchedLead(deleteLead.id)}
        deleteLoader={deleteMatchedLeadLoader}
        text="Do you really want to delete this Lead?"
      />
    </div>
  );
};

export default AllMatchesPage;
